import { z } from "zod";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import {
    EliminationExceptionReason,
    EliminationFactType,
} from "@common/types/domains/accounting/elimination";
import { JournalEntrySourceSchema } from "./journalEntry";

export const eliminationProvenanceSchema = z.object({
    journalEntryIds: z.array(z.string().uuid()),
    openingBalanceIds: z.array(z.string().uuid()).optional(),
});

export const eliminationSourceFactSchema = z.object({
    factType: z.nativeEnum(EliminationFactType),
    entityId: z.number().int(),
    otherEntityId: z.number().int(),
    accountId: z.string().uuid(),
    accountClass: z.nativeEnum(AccountClass),
    signedAmount: z.number(),
    provenance: eliminationProvenanceSchema,
});

export type EliminationSourceFactInput = z.infer<
    typeof eliminationSourceFactSchema
>;

export const eliminationPairSchema = z.object({
    entityId: z.number().int(),
    otherEntityId: z.number().int(),
});

export const eliminationEntrySchema = z.object({
    pair: eliminationPairSchema,
    matchGroupKey: z.string(),
    factType: z.nativeEnum(EliminationFactType),
    accountId: z.string().uuid(),
    accountClass: z.nativeEnum(AccountClass),
    amount: z.number(),
    provenance: eliminationProvenanceSchema,
    date: z.string().optional(),
});

export const eliminationExceptionSideSchema = z.object({
    entityId: z.number().int(),
    otherEntityId: z.number().int(),
    accountId: z.string().uuid(),
    signedAmount: z.number(),
    provenance: eliminationProvenanceSchema,
});

export const eliminationExceptionSchema = z.object({
    reason: z.nativeEnum(EliminationExceptionReason),
    factType: z.nativeEnum(EliminationFactType),
    pair: eliminationPairSchema,
    sides: z.array(eliminationExceptionSideSchema),
    netAmount: z.number(),
});

export const eliminationResultSchema = z.object({
    eliminations: z.array(eliminationEntrySchema),
    exceptions: z.array(eliminationExceptionSchema),
});

export type EliminationResultResponse = z.infer<typeof eliminationResultSchema>;

export const eliminationOffsetAccountSchema = z.object({
    id: z.string().uuid(),
    code: z.number().int().nullable(),
    codeText: z.string().nullable(),
    name: z.string(),
});

export type EliminationOffsetAccount = z.infer<
    typeof eliminationOffsetAccountSchema
>;

export const eliminationDetailJournalEntrySchema = z.object({
    journalEntryId: z.string().uuid(),
    referenceNumber: z.string(),
    accountingLedgerId: z.string().uuid(),
    offsetAccounts: z.array(eliminationOffsetAccountSchema),
});

export type EliminationDetailJournalEntry = z.infer<
    typeof eliminationDetailJournalEntrySchema
>;

export const eliminationExceptionJournalEntrySchema =
    eliminationDetailJournalEntrySchema.extend({
        entityId: z.number().int(),
        source: JournalEntrySourceSchema.optional(),
    });

export type EliminationExceptionJournalEntry = z.infer<
    typeof eliminationExceptionJournalEntrySchema
>;

export const eliminationDetailRowSchema = z.object({
    id: z.string(),
    pair: eliminationPairSchema,
    matchGroupKey: z.string().nullable().default(null),
    date: z.string(),
    entityName: z.string(),
    otherEntityName: z.string(),
    // The transaction counterparty on the underlying journal-entry line (vendor
    // /customer), distinct from the paired entity in `pair`. Null when the line
    // carries no counterparty.
    counterpartyId: z.string().uuid().nullable().default(null),
    counterpartyName: z.string().nullable().default(null),
    accountId: z.string().uuid(),
    accountCode: z.number().int().nullable(),
    accountCodeText: z.string().nullable().optional(),
    accountName: z.string(),
    factType: z.nativeEnum(EliminationFactType),
    // "engine" for auto-computed eliminations, "manual" for entries posted to
    // the group's overlay ledger. Defaults to "engine" so existing consumers
    // are unaffected.
    origin: z.enum(["engine", "manual"]).default("engine"),
    source: JournalEntrySourceSchema.optional(),
    // Set on manual overlay rows, linking back to the eliminating entry.
    manualJournalEntryId: z.string().uuid().nullable().optional(),
    // Natural-signed (positive in the account's normal direction) so the
    // figures match the eliminations column on the face of the statement.
    amount: z.number(),
    // Per-account running total of `amount`, accumulated in the order rows are
    // returned. The final balance for an account equals that account's
    // eliminations figure on the consolidated statement.
    balance: z.number(),
    memo: z.string(),
    description: z.string(),
    split: z.string(),
    journalEntries: z.array(eliminationDetailJournalEntrySchema),
});

export const eliminationDetailOpeningBalanceSchema = z.object({
    accountId: z.string().uuid(),
    accountCode: z.number().int().nullable(),
    accountCodeText: z.string().nullable().optional(),
    accountName: z.string(),
    entityId: z.number().int(),
    entityName: z.string(),
    amount: z.number(),
});

export type EliminationDetailOpeningBalance = z.infer<
    typeof eliminationDetailOpeningBalanceSchema
>;

export const eliminationDetailReportResponseSchema = z.object({
    consolidationGroupId: z.string().uuid(),
    consolidationGroupName: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    rows: z.array(eliminationDetailRowSchema),
    openingBalances: z.array(eliminationDetailOpeningBalanceSchema).default([]),
});

export type EliminationDetailReportResponse = z.infer<
    typeof eliminationDetailReportResponseSchema
>;

// One entity's leg of an exception. A two-sided unmatched_ic carries two sides
// with different intercompany accounts (one per entity, per the pair-unique
// index), so the flip account cannot be flattened onto the row.
export const eliminationExceptionRowSideSchema = z.object({
    entityId: z.number().int(),
    entityName: z.string(),
    accountId: z.string().uuid(),
    accountCode: z.number().int().nullable(),
    accountCodeText: z.string().nullable().optional(),
    accountName: z.string(),
    signedAmount: z.number(),
});

export type EliminationExceptionRowSide = z.infer<
    typeof eliminationExceptionRowSideSchema
>;

export const eliminationExceptionSourceSchema = z.enum([
    "transaction",
    "manual_je",
]);

export const eliminationExceptionRowSchema = z.object({
    id: z.string(),
    reason: z.nativeEnum(EliminationExceptionReason),
    factType: z.nativeEnum(EliminationFactType),
    pair: eliminationPairSchema,
    entityName: z.string(),
    otherEntityName: z.string(),
    // Latest provenance journal-entry date across the exception's sides; null
    // when no source journal entry could be resolved.
    date: z.string().nullable(),
    // Best-effort line-level context aggregated from the exception's provenance
    // journal entries. An exception spans multiple lines/sides, so these are
    // populated only when unambiguous: counterparty when the provenance shares a
    // single one. Description and memo stay in their own fields; multiple
    // provenance values are joined with " / " within that field only.
    counterpartyName: z.string().nullable().default(null),
    description: z.string().default(""),
    memo: z.string().default(""),
    journalEntries: z.array(eliminationExceptionJournalEntrySchema).default([]),
    sides: z.array(eliminationExceptionRowSideSchema),
    // Derived from the presence of transaction ids on the underlying facts.
    source: eliminationExceptionSourceSchema,
    // Residual that did not eliminate (debit-positive).
    netAmount: z.number(),
    transactionIds: z.array(z.number().int()),
    // True for in_transit_transfer — informational, not an actionable error.
    isInformational: z.boolean(),
});

export type EliminationExceptionRow = z.infer<
    typeof eliminationExceptionRowSchema
>;

// A one-sided P&L leg on an otherwise matched pair: real third-party activity,
// surfaced informationally and never counted as an exception.
export const eliminationPassThroughRowSchema = z.object({
    id: z.string(),
    factType: z.nativeEnum(EliminationFactType),
    pair: eliminationPairSchema,
    entityName: z.string(),
    otherEntityName: z.string(),
    accountId: z.string().uuid(),
    accountCode: z.number().int().nullable(),
    accountCodeText: z.string().nullable().optional(),
    accountName: z.string(),
    signedAmount: z.number(),
    journalEntryId: z.string().uuid().nullable(),
});

export type EliminationPassThroughRow = z.infer<
    typeof eliminationPassThroughRowSchema
>;

export const eliminationExceptionsReportResponseSchema = z.object({
    consolidationGroupId: z.string().uuid(),
    consolidationGroupName: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    rows: z.array(eliminationExceptionRowSchema),
    passThroughs: z.array(eliminationPassThroughRowSchema),
});

export type EliminationExceptionsReportResponse = z.infer<
    typeof eliminationExceptionsReportResponseSchema
>;
